import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);

    // Folliq fix: click-to-toggle, keyboard operability, touch double-tap and
    // outside-click-to-close for items that have a submenu. Bound once at the
    // menu level (event delegation) rather than per-item so it keeps working
    // for items added/changed by the overflow "More" logic.
    this.addEventListener('click', this.#onMenuClick);
    this.addEventListener('keydown', this.#onMenuKeydown);
    document.addEventListener('click', this.#onDocumentClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();

    this.removeEventListener('click', this.#onMenuClick);
    this.removeEventListener('keydown', this.#onMenuKeydown);
    document.removeEventListener('click', this.#onDocumentClick, true);
    this.#clearCloseTimer();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Folliq fix: pending delayed-close timer (Bug B). A synchronous close on
   * pointerleave means the ~19px dead zone between the trigger's own box and
   * the clip-revealed top of the submenu (a gap the CSS "safety box" only
   * covers intermittently, since it depends on a movement-delta threshold
   * that doesn't reliably fire for every real pointer path) closes the menu
   * before the cursor completes the diagonal trip. Delaying the close and
   * cancelling it on any re-entry fixes this regardless of path or speed,
   * and doesn't depend on precisely matching the dead zone's geometry.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #closeTimer;

  /**
   * Folliq fix: whether the currently-open item was opened via click (vs.
   * hover). A click-opened menu must stay open regardless of hover state
   * until an explicit close (outside click, Escape, or clicking the trigger
   * again) — it must not be closed by a stray pointerleave.
   * @type {boolean}
   */
  #openedByClick = false;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  #clearCloseTimer() {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = undefined;
    }
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // Folliq fix: cancel any pending delayed close — the pointer re-entered
    // the menu (either the trigger or the submenu) before the close fired.
    this.#clearCloseTimer();

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // Folliq fix: a menu opened by click stays open regardless of hover
    // state — only an explicit close (outside click / Escape / re-click)
    // should dismiss it.
    if (this.#openedByClick) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    // Folliq fix (Bug B): don't close synchronously on pointerleave. Give the
    // pointer time to re-enter (covers the dead zone between the trigger and
    // the clip-revealed submenu, on any path, at any speed) and cancel this
    // timer in `activate()` if it does. 200ms matches common hover-intent
    // close delays and is imperceptible as a "closes too slowly" lag.
    this.#clearCloseTimer();
    const itemAtScheduleTime = this.#state.activeItem;
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      this.#deactivate(itemAtScheduleTime);
    }, 200);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#openedByClick = false;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Folliq fix — click interaction model.
   *
   * Items with a submenu: click toggles open/closed and never navigates
   * (the destination is still reachable via the "Shop All"-style link
   * inside the submenu). This also gives touch users a working first tap —
   * touch devices dispatch a synthetic pointerenter immediately before
   * click, but never a pointerleave, so without this the link would just
   * navigate on first tap with no way to see the submenu at all.
   *
   * Items without a submenu (Bundle & Save, Journal, About): untouched,
   * click navigates normally.
   *
   * @param {MouseEvent} event
   */
  #onMenuClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('[ref="menuitem"]');
    if (!link) return;
    const item = link.closest('.menu-list__list-item');
    if (!item) return;
    const submenu = findSubmenu(link);
    if (!submenu) return; // no submenu on this item — let it navigate normally

    const alreadyOpen = this.#state.activeItem === link && this.#openedByClick;
    const isTouchDevice = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

    if (isTouchDevice && alreadyOpen) {
      // Second tap on an already-open trigger: let it navigate through, per
      // the standard "tap reveals, tap again commits" touch pattern (touch
      // has no hover preview at all, so this is the only way to reach the
      // trigger's own destination without going via a submenu item).
      return;
    }

    event.preventDefault();

    if (!isTouchDevice && alreadyOpen) {
      // Desktop click is a pure toggle — second click closes.
      this.#openedByClick = false;
      this.#deactivate(link);
      link.focus();
      return;
    }

    this.#clearCloseTimer();
    this.activate(/** @type {any} */ ({ target: link }));
    this.#openedByClick = true;
  };

  /**
   * Folliq fix — outside click closes an open (click-opened or
   * hover-opened) menu. Uses the capture phase so it always sees the click
   * even if something inside stops propagation.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#state.activeItem) return;
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    this.#openedByClick = false;
    this.#clearCloseTimer();
    this.#deactivate();
  };

  /**
   * Folliq fix — keyboard operability.
   * Enter/Space on a trigger with a submenu: toggle, and on open move focus
   * into the first submenu link. ArrowDown: same as Enter when focus is on
   * the trigger; move to next link when focus is inside the submenu.
   * ArrowUp: move to previous link inside the submenu. Escape: close and
   * return focus to the trigger. Tab: let it flow naturally, but close the
   * menu once focus leaves it entirely (handled via focusout below).
   * @param {KeyboardEvent} event
   */
  #onMenuKeydown = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.key === 'Escape') {
      const active = this.#state.activeItem;
      if (!active) return;
      event.preventDefault();
      this.#openedByClick = false;
      this.#clearCloseTimer();
      this.#deactivate();
      active.focus();
      return;
    }

    const triggerLink = event.target.closest('[ref="menuitem"]');
    if (triggerLink) {
      const submenu = findSubmenu(triggerLink);
      if (!submenu) return;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.#clearCloseTimer();
        if (this.#state.activeItem !== triggerLink) {
          this.activate(/** @type {any} */ ({ target: triggerLink }));
          this.#openedByClick = true;
        }
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.#clearCloseTimer();
        if (this.#state.activeItem !== triggerLink) {
          this.activate(/** @type {any} */ ({ target: triggerLink }));
          this.#openedByClick = true;
        }
        const firstLink = submenu.querySelector('a, .mega-menu__link');
        if (firstLink instanceof HTMLElement) firstLink.focus();
        return;
      }
      return;
    }

    // Focus is inside an open submenu
    const openSubmenu = findSubmenu(this.#state.activeItem);
    if (openSubmenu && openSubmenu.contains(event.target)) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const links = Array.from(openSubmenu.querySelectorAll('a, .mega-menu__link')).filter(
          (el) => el instanceof HTMLElement
        );
        const currentIndex = links.indexOf(/** @type {any} */ (event.target));
        if (currentIndex === -1) return;
        const nextIndex =
          event.key === 'ArrowDown' ? (currentIndex + 1) % links.length : (currentIndex - 1 + links.length) % links.length;
        /** @type {HTMLElement} */ (links[nextIndex]).focus();
        return;
      }

      // Folliq fix: Enter on a focused submenu link doesn't reliably reach
      // the browser's native "Enter on a focused <a> triggers navigation"
      // behavior in this theme framework (something upstream — likely the
      // Component base class's generic on:click handling — intercepts and
      // prevents the default keydown before it gets here). Force navigation
      // explicitly rather than leaving Enter as a dead key on a real link.
      if (event.key === 'Enter' && event.target instanceof HTMLAnchorElement) {
        event.preventDefault();
        window.location.href = event.target.href;
      }
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
